import type { ComponentType, SVGProps } from 'react';
import getImageIcon from '../icons/ImageIcon';
import { iconRegistry } from './generated/export';

const isErrorLogged: Record<string, boolean> = {};

// distinguishes image URLs (app-relative, absolute, or inline data URIs)
// from icon registry keys so they render via <img> instead of a registry lookup
const shouldRenderAsImage = (iconName?: string) => {
  if (!iconName) {
    return false;
  }
  return (
    iconName.startsWith('/') || iconName.startsWith('https://') || iconName.startsWith('data:')
  );
};

const EXTENSIONS = ['.png', '.jpg', '.jpeg', '.gif', '.webp'];

const isAnImage = (iconName?: string) => {
  if (!iconName) {
    return false;
  }

  return shouldRenderAsImage(iconName) && EXTENSIONS.some((ext) => iconName.endsWith(ext));
};

export const getIconFromRegistry = (
  iconName?: string,
  ImageIcon?: ComponentType<SVGProps<SVGSVGElement>>,
): ComponentType<SVGProps<SVGSVGElement>> | undefined => {
  if (!iconName) {
    return undefined;
  }

  if (isAnImage(iconName)) {
    /**
     * This is done to fix a case of incorrect config - when people use image urls in icon block using enter custom value.
     *
     * Earlier, custom icons were being rendered as image but because of this, the color property was not working in icon.
     * and because of this same reason, entering image url in component.content.icon worked!
     *
     * To fix the icon color bug, we started rendering custom icons using InlineSvg.
     *
     * Now the cases where people had incorrectly inserted image urls directly into content.icon, broke.
     * Hence adding this fallback to avoid those instances breaking from live apps.
     */
    return getImageIcon({ iconName });
  }

  if (shouldRenderAsImage(iconName)) {
    // In Automations, we do not send the ImageIcon override so that the icons for the apps are rendered as images only.
    return ImageIcon ?? getImageIcon({ iconName });
  }

  const icon = iconRegistry[iconName]?.icon;

  if (!icon) {
    if (!isErrorLogged[iconName]) {
      console.error(`Icon not found for ${iconName}`);
      isErrorLogged[iconName] = true;
    }
    return undefined;
  }

  return icon;
};
